import { ReactElement } from 'react';
import { Component as SaleTagIcon } from 'icons/icon-ui--sale-tag.svg';
import { Component as PlusIcon } from 'icons/icon-ui--plus.svg';
import { parsePricePerRecipe } from 'utils/prices';
import { useNavigateWithUTMs } from '../../hooks/useNavigateWithUTMs';
import { useAuth } from '../../contexts/AuthContext';
import { QuantitySelector } from '../shared/quantity-selector/QuantitySelector';
import styles from './CartItem.module.scss';
import { Button } from '../shared/button/Button';
import { gtmTrackAddToCart } from '../../services/gtm/gtm';

type Props = {
  quantity: number;
  pricePerServing?: string;
  servingSize?: number;
  title: string;
  description?: string;
  imageUrl: string;
  totalPrice: string;
  skuPrice?: string;
  isCartSkipped?: boolean;
  isSaleTagEnabled: boolean;
  salePrice: string;
  salePricePerServing?: string;
  isActive: boolean;
  onUpdateCartQuantity: (quantity: number) => void;
  heapId: string;
  skuId: number;
  path: string;
  onClickImage: () => void;
  isLtoBox: boolean;
  ltoServings: string;
  isPromoItem: boolean;
  onAddToCart?: () => void;
};

export function CartItem({
  salePricePerServing,
  quantity,
  pricePerServing,
  servingSize,
  title,
  description,
  imageUrl,
  totalPrice,
  skuPrice,
  isCartSkipped = false,
  isSaleTagEnabled,
  salePrice,
  isActive = true,
  onUpdateCartQuantity,
  heapId,
  skuId,
  path,
  onClickImage,
  isLtoBox,
  ltoServings,
  isPromoItem,
  onAddToCart,
}: Props): ReactElement {
  const { isLoggedIn } = useAuth();
  const { navigate } = useNavigateWithUTMs();
  const handleClick = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    navigate(path, { state: { skuId } });
    if (onClickImage) {
      onClickImage();
    }
  };

  return (
    <div className={styles.cartItem} data-heap-id={heapId}>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleClick();
          }
        }}
        className={styles.image_wrapper}
        onClick={handleClick}
      >
        <img className={styles.product_image} src={imageUrl} alt="temp-img" />
        {isSaleTagEnabled && salePrice && (
          <div className={styles.sale_tag}>
            <SaleTagIcon />
            SALE
          </div>
        )}
      </div>
      <div className={styles.text_content}>
        <div className={styles.title_text}>
          {title}
          {!isLtoBox && pricePerServing && servingSize && (
            <div className={styles.left_content}>
              <div className={styles.serving_description_text}>
                <div className={styles.price_row}>
                  {isSaleTagEnabled && salePricePerServing ? (
                    <>
                      <span className={styles.sale_price}>{salePricePerServing}</span>
                      <span className={styles.pre_sale_price}>{pricePerServing}</span>
                    </>
                  ) : (
                    <span>{pricePerServing}</span>
                  )}
                </div>
                <span>
                  {' '}
                  / serving • feeds
                  {' '}
                  {servingSize}
                </span>
              </div>
            </div>
          )}
          {isLtoBox && description && ltoServings && (
            <div className={styles.left_content}>
              <div className={styles.serving_description_text}>
                {`${description} / serves ${ltoServings}`}
              </div>
            </div>
          )}
        </div>

        {!isPromoItem && (
          <div className={styles.product_tag_quantity_selector_container}>
            <div className={styles.price}>
              {totalPrice}
            </div>
            {isCartSkipped || !isActive ? (
              <QuantitySelector
                quantity={quantity}
                onAddQuantity={() => null}
                onSubtractQuantity={() => null}
              />
            ) : (
              <QuantitySelector
                quantity={quantity}
                onAddQuantity={() => {
                  if (!isLoggedIn) {
                    gtmTrackAddToCart({
                      price: parsePricePerRecipe(pricePerServing || totalPrice),
                      currency: 'USD',
                      ecomPagetype: 'basket',
                      item: {
                        itemName: title,
                        itemId: String(skuId),
                        price: parsePricePerRecipe(pricePerServing || totalPrice),
                        quantity: 1,
                      },
                    });
                  }
                  onUpdateCartQuantity(quantity + 1);
                }}
                onSubtractQuantity={() => onUpdateCartQuantity(quantity - 1)}
              />
            )}
          </div>
        )}

        {isPromoItem && (
          <div className={styles.product_tag_quantity_selector_container}>
            <div className={styles.price}>
              {skuPrice || totalPrice}
            </div>
            <Button
              text="Add"
              icon={PlusIcon}
              onClick={onAddToCart}
              className={styles.button_styling}
              heapId="add_button"
            />
          </div>
        )}

      </div>
    </div>

  );
}
